import { configureStore } from "@reduxjs/toolkit";

import { GlobalErrorEvents } from "src/errorHandling";
import { appInitAction } from "src/listenerMiddleware";
import { rootApi, rtkQueryErrorAuth, rtkQueryErrorLogger } from "src/serverApi";
import { EMPTY_OBJECT, isDevelopment } from "src/utils";
import { listenerMiddleware } from "./listenerMiddleware";
import { getRootReducer } from "./reducer";

/**
 * Creates redux store with root reducer and RTK Query middleware and redux listener middleware.
 * Sets up top level error handling for root reducer and redux listener middleware.
 * Dispatches <code>appInitAction</code> after store is created.
 */
const createStore = () => {
    const rootReducer = getRootReducer();
    const errorHandlingReducer: typeof rootReducer = (state, action) => {
        try {
            return rootReducer(state, action);
        } catch (error) {
            GlobalErrorEvents.triggerReducerErrorEvent(error);
            /* Error at this place is unrecoverable, app cannot be used anymore and ErrorPage is displayed.
             * For this reason we can return previous state or empty object and type cast it. */
            return (state ?? EMPTY_OBJECT) as StoreState;
        }
    };

    const store = configureStore({
        reducer: errorHandlingReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: true,
                immutableCheck: false,
            }).prepend(rootApi.middleware, listenerMiddleware.middleware, rtkQueryErrorAuth, rtkQueryErrorLogger),
        devTools: isDevelopment,
    });

    store.dispatch(appInitAction());

    return store;
};

/**
 * Application store with RTK Query middleware and redux listener middleware.
 * Use as prop for react-redux Provider component.
 */
export const store = createStore();

export type StoreState = ReturnType<typeof store.getState>;
export type StoreDispatch = typeof store.dispatch;
